/* eslint-disable replo/no-tailwind-literal-color */
/* eslint-disable replo/no-v1-imports */
/* oxlint-disable eslint-plugin-replo/no-v1-imports -- no v2 chart component exists yet */
import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { MemoryStatsPayload } from "schemas/generated/agentConnection";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxHeader,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { Field, FieldLabel } from "@replo/design-system/components/field/Field";
import {
  ChartContainer,
  ChartTooltipContent,
} from "@v1/components/designSystem/shadcn/core/chart";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatBytes } from "replo-utils/lib/math";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

function isFiniteMemoryLimit(bytes: number): boolean {
  return bytes > 0 && bytes < Number.MAX_SAFE_INTEGER;
}

function formatTimeAxis(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, {
    minute: "2-digit",
    second: "2-digit",
  });
}

const CONTAINER_CHART_CONFIG = {
  current: { label: "Used", color: "#3b82f6" },
  max: { label: "Max", color: "#e5e7eb" },
};

const PROCESS_LINE_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
];

const MemoryContent: React.FC<{
  snapshots: Record<string, MemoryStatsPayload[]>;
}> = ({ snapshots }) => {
  const sandboxIds = Object.keys(snapshots);
  const [selectedSandboxId, setSelectedSandboxId] = React.useState<
    string | null
  >(sandboxIds[0] ?? null);

  useOnValueChange(sandboxIds.length, (_current, previous) => {
    if (previous === 0 && sandboxIds.length > 0) {
      setSelectedSandboxId(sandboxIds[0] ?? null);
    }
  });

  const sandboxOptions = React.useMemo(() => {
    return sandboxIds.map((id) => {
      const sandboxSnapshots = snapshots[id];
      const latest = sandboxSnapshots?.[sandboxSnapshots.length - 1];
      const truncatedId = id.length > 20 ? `${id.slice(0, 20)}…` : id;
      if (latest && isFiniteMemoryLimit(latest.containerMaxBytes)) {
        const usedMB = Math.round(latest.containerCurrentBytes / (1024 * 1024));
        const totalMB = Math.round(latest.containerMaxBytes / (1024 * 1024));
        return {
          value: id,
          label: `${truncatedId} (${usedMB}MB/${totalMB}MB)`,
        };
      }
      return { value: id, label: truncatedId };
    });
  }, [sandboxIds, snapshots]);

  const selectedSnapshots = selectedSandboxId
    ? snapshots[selectedSandboxId] ?? []
    : [];

  const hasMemoryLimit = selectedSnapshots.some((s) => {
    return isFiniteMemoryLimit(s.containerMaxBytes);
  });

  const containerChartData = React.useMemo(() => {
    return selectedSnapshots.map((s) => ({
      time: s.timestamp,
      current: s.containerCurrentBytes / (1024 * 1024),
      ...(hasMemoryLimit
        ? { max: s.containerMaxBytes / (1024 * 1024) }
        : undefined),
    }));
  }, [selectedSnapshots, hasMemoryLimit]);

  const processNames = React.useMemo(() => {
    const names = new Set<string>();
    for (const s of selectedSnapshots) {
      for (const name of Object.keys(s.processes)) {
        names.add(name);
      }
    }
    return [...names];
  }, [selectedSnapshots]);

  const processChartConfig = React.useMemo(() => {
    const config: Record<string, { label: string; color: string }> = {};
    for (let i = 0; i < processNames.length; i++) {
      const name = processNames[i]!;
      config[name] = {
        label: name,
        color: PROCESS_LINE_COLORS[i % PROCESS_LINE_COLORS.length]!,
      };
    }
    return config;
  }, [processNames]);

  const processChartData = React.useMemo(() => {
    return selectedSnapshots.map((s) => {
      const point: Record<string, number> = { time: s.timestamp };
      for (const name of processNames) {
        point[name] = (s.processes[name]?.rssBytes ?? 0) / (1024 * 1024);
      }
      return point;
    });
  }, [selectedSnapshots, processNames]);

  if (sandboxIds.length === 0) {
    return (
      <span className="text-xs text-slate-400">
        No memory data received yet
      </span>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-auto">
      <Field size="xs">
        <FieldLabel size="xs">Sandbox</FieldLabel>
        <ComboboxRoot
          items={sandboxOptions}
          value={
            selectedSandboxId
              ? sandboxOptions.find((o) => o.value === selectedSandboxId) ??
                null
              : null
          }
          onValueChange={(item) => {
            if (item) {
              setSelectedSandboxId(item.value);
            }
          }}
          itemToStringValue={(item) => String(item.label)}
          isItemEqualToValue={(a, b) => a.value === b.value}
        >
          <ComboboxTriggerButton size="sm">
            {sandboxOptions.find((o) => o.value === selectedSandboxId)?.label ??
              "Select sandbox"}
          </ComboboxTriggerButton>
          <ComboboxContent UNSAFE_className="!z-[2147483647] w-60">
            <ComboboxHeader>
              <ComboboxInput placeholder="Search..." />
            </ComboboxHeader>
            <ComboboxEmpty />
            <ComboboxList>
              {(item: ComboboxItemWithValue) => (
                <ComboboxItem key={item.value} {...item} value={item} />
              )}
            </ComboboxList>
          </ComboboxContent>
        </ComboboxRoot>
      </Field>

      {selectedSnapshots.length > 0 && (
        <>
          <div>
            <div className="mb-1 text-xs font-semibold text-slate-500">
              Container Memory (MB)
            </div>
            <ChartContainer
              config={CONTAINER_CHART_CONFIG}
              className="h-[140px] w-full"
            >
              <AreaChart
                data={containerChartData}
                margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  stroke="#e5e7eb"
                  horizontal={true}
                  vertical={false}
                />
                <XAxis
                  dataKey="time"
                  tickFormatter={formatTimeAxis}
                  tick={{ fill: "#94a3b8", fontSize: 9 }}
                  tickLine={false}
                  stroke="#e5e7eb"
                />
                <YAxis
                  tickFormatter={(v: number) => `${Math.round(v)}`}
                  tick={{ fill: "#94a3b8", fontSize: 9 }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  content={
                    <ChartTooltipContent
                      className="bg-white"
                      labelFormatter={(
                        _value: string,
                        payload: { payload?: { time?: number } }[],
                      ) => {
                        const time = payload?.[0]?.payload?.time;
                        return time ? formatTimeAxis(Number(time)) : "";
                      }}
                      valueFormatter={(value: number) => {
                        return formatBytes(value * 1024 * 1024);
                      }}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="max"
                  stroke="#d1d5db"
                  fill="#f1f5f9"
                  strokeWidth={1}
                  strokeDasharray="4 2"
                  dot={false}
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="current"
                  stroke="#3b82f6"
                  fill="#dbeafe"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ChartContainer>
          </div>

          {processNames.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-slate-500">
                Per-Process RSS (MB)
              </div>
              <ChartContainer
                config={processChartConfig}
                className="h-[140px] w-full"
              >
                <LineChart
                  data={processChartData}
                  margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
                >
                  <CartesianGrid
                    stroke="#e5e7eb"
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="time"
                    tickFormatter={formatTimeAxis}
                    tick={{ fill: "#94a3b8", fontSize: 9 }}
                    tickLine={false}
                    stroke="#e5e7eb"
                  />
                  <YAxis
                    tickFormatter={(v: number) => `${Math.round(v)}`}
                    tick={{ fill: "#94a3b8", fontSize: 9 }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    content={
                      <ChartTooltipContent
                        className="bg-white"
                        labelFormatter={(
                          _value: string,
                          payload: { payload?: { time?: number } }[],
                        ) => {
                          const time = payload?.[0]?.payload?.time;
                          return time ? formatTimeAxis(Number(time)) : "";
                        }}
                        valueFormatter={(value: number) => {
                          return formatBytes(value * 1024 * 1024);
                        }}
                      />
                    }
                  />
                  {processNames.map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={
                        PROCESS_LINE_COLORS[i % PROCESS_LINE_COLORS.length]
                      }
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ChartContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MemoryTab: React.FC<{
  snapshots: Record<string, MemoryStatsPayload[]>;
  onClearStats: () => void;
}> = ({ snapshots, onClearStats }) => {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 text-xs">
      <MemoryContent snapshots={snapshots} />
      {Object.keys(snapshots).length > 0 && (
        <Button
          variant="secondary"
          size="sm"
          UNSAFE_className="shrink-0 self-start"
          onClick={onClearStats}
        >
          Clear data
        </Button>
      )}
    </div>
  );
};

export { MemoryTab };
